/**
 * Per-frame content metrics for the fight-pose art (`assets/fimbulwake/fight/<class>/<pose>.webp`).
 *
 * QA (86cat3wgq): "fighters render at different sizes, feet don't sit on the
 * same line." Root cause is in the ART, not the layout — every pose is an
 * independently generated cutout, not frames of one shared template, so each
 * one bakes in its OWN transparent margin (headroom above the head, a gap
 * below the feet) and its OWN canvas aspect ratio. FightScreen used to render
 * every pose at a flat `height: 100%` of the fighter box; that scales the
 * baked-in padding right along with the character, so two fighters standing
 * in the identical box read as different heights, feet at different levels —
 * exactly the QA report (measured: warrior's default frame carries ~7.9%
 * bottom padding, paladin's only ~4.2% — at the same box height that's a
 * visible few-percent difference in where the feet land).
 *
 * `c` — the character's own silhouette height, as a fraction of the full
 *       image height (from the top of the alpha bounding box to the bottom).
 * `b` — the transparent gap below the feet, as a fraction of the full image
 *       height (i.e. how much of the canvas is empty floor/shadow-less air
 *       under the character).
 *
 * Measured once against the shipped webp set (alpha-channel bounding box per
 * file) and baked in here — this is presentation metadata about static art,
 * not gameplay data, so it lives beside the UI that consumes it rather than
 * re-scanning a canvas on every pose swap during a fight.
 */
export const FIGHT_POSE_METRICS: Record<string, Record<string, { c: number; b: number }>> = {
  ARCHER: {
    // win + ability re-measured 02.09: both had the bow's upper limb chopped
    // flat by the canvas top (owner: "the archer's victory sprite is clipped").
    // The missing wood was outpainted back and the frame refitted, so the
    // silhouette is a touch shorter than the old 0.95 — the bow now ends in a
    // nock instead of at the edge.
    'ability': { c: 0.9367, b: 0.05 },
    'damage-2': { c: 0.8667, b: 0.055 },
    'damage': { c: 0.8483, b: 0.0517 },
    'default': { c: 0.885, b: 0.05 },
    'defeat': { c: 0.7267, b: 0.1083 },
    'flee': { c: 0.875, b: 0.0583 },
    'guard': { c: 0.8617, b: 0.1 },
    'heavy': { c: 0.9217, b: 0.055 },
    'patch-up': { c: 0.8533, b: 0.075 },
    'strike': { c: 0.8883, b: 0.085 },
    'win': { c: 0.9367, b: 0.05 },
  },
  // The four mythic world bosses (CU-86cbc65ny follow-up: full pose sets).
  // Measured the same way as every other set — alpha bbox per shipped webp.
  'DRAUGR-KING': {
    'damage': { c: 0.9083, b: 0.0433 },
    'default': { c: 0.9383, b: 0.0133 },
    'defeat': { c: 0.715, b: 0.1517 },
    'guard': { c: 0.9383, b: 0.015 },
    'heavy': { c: 0.9267, b: 0.0367 },
    'strike': { c: 0.8683, b: 0.0433 },
    'win': { c: 0.935, b: 0.035 },
  },
  'FENRIR-SPAWN': {
    'damage': { c: 0.7117, b: 0.1283 },
    'default': { c: 0.7217, b: 0.1133 },
    'defeat': { c: 0.3733, b: 0.2733 },
    'guard': { c: 0.7167, b: 0.1117 },
    'heavy': { c: 0.8333, b: 0.08 },
    'strike': { c: 0.6983, b: 0.15 },
    'win': { c: 0.8633, b: 0.0617 },
  },
  'FROST-JOTUNN': {
    'damage': { c: 0.9383, b: 0 },
    'default': { c: 0.9383, b: 0 },
    'defeat': { c: 0.8833, b: 0.055 },
    'guard': { c: 0.915, b: 0 },
    'heavy': { c: 0.8767, b: 0.0533 },
    'strike': { c: 0.92, b: 0 },
    'win': { c: 0.9217, b: 0.0567 },
  },
  'JORMUNGANDR-COIL': {
    'damage': { c: 0.8833, b: 0.0567 },
    'default': { c: 0.8833, b: 0.0567 },
    'defeat': { c: 0.885, b: 0.055 },
    'guard': { c: 0.7883, b: 0.13 },
    'heavy': { c: 0.8833, b: 0.0567 },
    'strike': { c: 0.8833, b: 0.0567 },
    'win': { c: 0.8833, b: 0.0567 },
  },
  BRUISER: {
    'damage': { c: 0.8933, b: 0.0317 },
    'default': { c: 0.8833, b: 0.0317 },
    // defeat re-measured 02.09 — the outflung arm ran off the left edge; it was
    // outpainted back, and a kneeling figure that wide has to sit lower.
    'defeat': { c: 0.685, b: 0.075 },
    'guard': { c: 0.9467, b: 0.0317 },
    'heavy': { c: 0.93, b: 0.035 },
    'strike': { c: 0.8633, b: 0.055 },
    'win': { c: 0.9467, b: 0.0333 },
  },
  GOON: {
    'damage': { c: 0.8667, b: 0.0417 },
    'default': { c: 0.9, b: 0.0367 },
    'defeat': { c: 0.755, b: 0.1317 },
    'guard': { c: 0.8817, b: 0.0467 },
    'heavy': { c: 0.925, b: 0.0433 },
    'strike': { c: 0.9017, b: 0.0367 },
    'win': { c: 0.93, b: 0.045 },
  },
  HITMAN: {
    'damage': { c: 0.8683, b: 0.0817 },
    'default': { c: 0.8867, b: 0.0467 },
    'defeat': { c: 0.79, b: 0.0733 },
    'guard': { c: 0.8867, b: 0.0467 },
    'heavy': { c: 0.8317, b: 0.1033 },
    'strike': { c: 0.8867, b: 0.0467 },
    'win': { c: 0.91, b: 0.0483 },
  },
  KINGPIN: {
    'damage': { c: 0.82, b: 0.07 },
    'default': { c: 0.88, b: 0.055 },
    'defeat': { c: 0.8117, b: 0.07 },
    'guard': { c: 0.8817, b: 0.0633 },
    'heavy': { c: 0.9067, b: 0.0483 },
    'strike': { c: 0.8267, b: 0.0633 },
    'win': { c: 0.9083, b: 0.06 },
  },
  LEGBREAKER: {
    'damage': { c: 0.88, b: 0.045 },
    'default': { c: 0.895, b: 0.045 },
    'defeat': { c: 0.845, b: 0.06 },
    'guard': { c: 0.85, b: 0.0683 },
    'heavy': { c: 0.905, b: 0.05 },
    'strike': { c: 0.8133, b: 0.06 },
    'win': { c: 0.9383, b: 0.05 },
  },
  MAGE: {
    'ability': { c: 0.9, b: 0.0367 },
    'damage': { c: 0.87, b: 0.0367 },
    'default': { c: 0.92, b: 0.0367 },
    'defeat': { c: 0.75, b: 0.1367 },
    'flee': { c: 0.8933, b: 0.045 },
    'guard': { c: 0.8917, b: 0.0467 },
    'heavy': { c: 0.8267, b: 0.0367 },
    'patch-up': { c: 0.9, b: 0.0367 },
    'strike': { c: 0.8833, b: 0.05 },
    'win': { c: 0.9117, b: 0.045 },
  },
  PALADIN: {
    'ability': { c: 0.8933, b: 0.0483 },
    'damage': { c: 0.8, b: 0.0767 },
    'default': { c: 0.9067, b: 0.0467 },
    'defeat': { c: 0.8583, b: 0.03 },
    'flee': { c: 0.8617, b: 0.0733 },
    // win + guard re-measured 01.09 — win regenerated (sword tip was cut by
    // the canvas top), guard's blade locally extended past the old right-edge
    // slice (owner: "то меч, то ещё что-то обрезается").
    'guard': { c: 0.865, b: 0.0517 },
    'heavy': { c: 0.9033, b: 0.0467 },
    'patch-up': { c: 0.9067, b: 0.0383 },
    'strike': { c: 0.78, b: 0.0833 },
    'win': { c: 0.93, b: 0.0467 },
  },
  THUG: {
    'damage': { c: 0.895, b: 0.0367 },
    'default': { c: 0.9117, b: 0.0367 },
    'defeat': { c: 0.7633, b: 0.1217 },
    'guard': { c: 0.88, b: 0.04 },
    'heavy': { c: 0.9217, b: 0.035 },
    'strike': { c: 0.9133, b: 0.035 },
    'win': { c: 0.9317, b: 0.0383 },
  },
  WARRIOR: {
    'ability': { c: 0.8783, b: 0.0583 },
    'damage-2': { c: 0.8733, b: 0.0583 },
    'damage': { c: 0.87, b: 0.0583 },
    'default': { c: 0.8767, b: 0.0583 },
    'defeat': { c: 0.8383, b: 0.065 },
    'flee': { c: 0.8467, b: 0.0717 },
    'guard': { c: 0.8917, b: 0.0583 },
    'heavy': { c: 0.885, b: 0.06 },
    'patch-up': { c: 0.845, b: 0.0767 },
    'strike': { c: 0.8433, b: 0.07 },
    'win': { c: 0.9217, b: 0.0567 },
  },
};

// ── The elite bestiary (72 beasts, frames shot 02.09) ──────────────────────
// Unlike the classes, these were not measured one frame at a time: every elite
// frame is normalised on install to ONE geometry — silhouette centred, 4% of
// the canvas left clear under the feet — so the only figure that varies per
// beast is its idle silhouette's height, which is what poseFit scales the set
// from. A beast too wide to stand 90% tall (an eel, a moth, a spread-winged
// eagle) comes out shorter here; that is the art, not a measuring error.
const ELITE_FOOT_GAP = 0.04;
const ELITE_POSES = ['default', 'strike', 'damage', 'win', 'defeat'];
const ELITE_CONTENT: Record<string, number> = {
  'wharf-lindworm': 0.875, 'frost-tusk-walrus': 0.8333, 'hook-draugr': 0.9,
  'fog-widow': 0.8967, 'icebreak-troll': 0.8967, 'claw-crab': 0.7633,
  'gallows-raven': 0.895, 'noose-hound': 0.8967, 'gallows-goat': 0.8983,
  'hanged-draugr': 0.9, 'keening-shade': 0.8967, 'gibbet-troll': 0.9,
  'thingstead-boar': 0.865, 'stall-elk': 0.8983, 'oathbreaker-draugr': 0.8983,
  'weighing-wraith': 0.9, 'stall-troll': 0.8233, 'grain-weevil': 0.85,
  'odal-wolfhound': 0.8617, 'boundary-stag': 0.8967, 'oathkin-draugr': 0.9,
  'kin-shade': 0.8917, 'boundary-troll': 0.8783, 'watch-hawk': 0.87,
  'forge-troll': 0.8233, 'bellows-ram': 0.9, 'furnace-lynx': 0.8967,
  'smith-draugr': 0.8967, 'ember-wisp': 0.895, 'forge-wasp': 0.895,
  'sledge-ox': 0.7317, 'runner-wolf': 0.7717, 'cargo-bull': 0.785,
  'cargo-wraith': 0.9, 'yard-jotunn': 0.9, 'frost-shrike': 0.8967,
  'ash-wraith': 0.9, 'cinder-fox': 0.865, 'ash-boar': 0.7933,
  'pyre-draugr': 0.8983, 'ember-jotunn': 0.8983, 'ash-moth': 0.6183,
  'mead-bear': 0.8983, 'brawler-steed': 0.8983, 'bench-draugr': 0.9,
  'skald-shade': 0.8967, 'cellar-troll': 0.7983, 'mead-wasp': 0.8133,
  'blood-wyrm': 0.9, 'vendetta-hound': 0.8983, 'feud-draugr': 0.8967,
  'vein-wraith': 0.8967, 'debt-troll': 0.8983, 'blood-fly': 0.8267,
  'keel-kraken': 0.8967, 'prow-ram': 0.8967, 'yard-mastiff': 0.8983,
  'shipwright-draugr': 0.9, 'rigging-wraith': 0.9, 'keelhaul-jotunn': 0.8983,
  'drowned-shade': 0.8983, 'wreck-otter': 0.8967, 'fjord-eel': 0.455,
  'wreck-draugr': 0.8983, 'fjord-troll': 0.9, 'pier-cormorant': 0.8617,
  'thing-eagle': 0.8983, 'summit-goat': 0.8983, 'storm-ram': 0.7867,
  'jarl-draugr': 0.8933, 'wind-shade': 0.8983, 'seatguard-jotunn': 0.8717,
};
for (const [slug, c] of Object.entries(ELITE_CONTENT)) {
  FIGHT_POSE_METRICS[slug.toUpperCase()] = Object.fromEntries(
    ELITE_POSES.map((pose) => [pose, { c, b: ELITE_FOOT_GAP }]),
  );
}

/** Floor for the reference content fraction — pure safety net for a future
 *  class whose measured default frame comes in absurdly padded. Every shipped
 *  class default is ≥ 0.87; the floor does bite for the two elites whose art
 *  is far wider than it is tall (the Fjord Eel, the Ash Moth) — they render a
 *  touch smaller than their box rather than ballooning to fill it. */
const MIN_CONTENT = 0.75;

/**
 * CSS values that render every pose of a fighter at ONE constant art scale,
 * with the feet on the box's own bottom edge.
 *
 * `height` is derived from the class's DEFAULT frame only: all pose canvases
 * in a set share the same 448x600 canvas, so giving every pose the same CSS
 * height keeps the character's scale identical across swaps. What changes
 * between poses is the SILHOUETTE — a recoiling or kneeling figure is simply
 * shorter — which is exactly what should read on screen. (The first cut
 * normalized each pose's own bounding box to the box height instead; that
 * inflated a crouched `damage`/`defeat` frame by up to ~30% to "fill" the
 * box, so the fighter visibly grew every time a hit landed.)
 *
 * `translateY` (a percentage of the element's OWN rendered height —
 * scale-invariant) stays per-pose: it slides the image down by its baked-in
 * foot-to-canvas-edge gap so the feet — not the transparent margin below
 * them — land on the box's bottom edge. The box is bottom-anchored via
 * `align-items: flex-end` (see .fs-fighter), so sliding down by the residual
 * bottom padding is what actually plants the feet on the ground line.
 *
 * Falls back to identity for anything not in the table above — a new pose
 * without measurements never breaks, it just isn't corrected yet.
 */
export function poseFit(cls?: string | null, pose?: string | null): { height: string; translateY: number } {
  const set = cls ? FIGHT_POSE_METRICS[cls.toUpperCase()] : undefined;
  const m = set && pose ? set[pose] : undefined;
  if (!set || !m) return { height: '100%', translateY: 0 };
  const ref = Math.max(set['default']?.c ?? m.c, MIN_CONTENT);
  return { height: `calc(100% / ${ref})`, translateY: Math.round(m.b * 10000) / 100 };
}
